import { useState, useEffect, useMemo } from 'react'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useInventoryDashboardLayoutStore } from '../stores/inventoryDashboardLayoutStore'
import {
    Chart as ChartJS, CategoryScale, LinearScale,
    LineElement, LineController, PointElement, Tooltip, Legend, Filler,
    ArcElement, PieController
} from 'chart.js'
import { api, apiPost } from '../lib/api'
import { fmt } from '../lib/dashboardHelpers'
import type { StoreConfig } from '../types/dashboard'
import InventoryLevelChart from '../components/dashboard/InventoryLevelChart'
import DepartmentPieChart from '../components/dashboard/DepartmentPieChart'
import StockoutInsightsPanel from '../components/dashboard/StockoutInsightsPanel'
import WidgetGrid from '../components/dashboard/WidgetGrid'
import { SqlDebugModal } from '../components/dashboard'
import WidgetGallery from '../components/WidgetGallery'
import LayoutPresetsMenu from '../components/dashboard/LayoutPresetsMenu'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import '../styles/dashboard.css'
import '../styles/widget-gallery.css'

ChartJS.register(CategoryScale, LinearScale, LineElement, LineController, PointElement, Tooltip, Legend, Filler, ArcElement, PieController)

interface InventoryLevelPoint {
    period: string; totalQuantity: number; uniqueSKUs: number; inventoryValue: number;
    lyTotalQuantity: number; lyUniqueSKUs: number; lyInventoryValue: number;
}
interface SnapshotDate { date: string; label: string }
interface InventorySummary {
    totalUnits: number; uniqueSKUs: number; inventoryValue: number; snapshotDate: string | null
    priorTotalUnits: number; priorUniqueSKUs: number; priorInventoryValue: number; priorSnapshotDate: string | null
    lyTotalUnits: number; lyUniqueSKUs: number; lyInventoryValue: number; lySnapshotDate: string | null
}
interface Brand { id: string; name: string }
interface StockoutItem {
    itemCardId: number; description: string; styleCode: string; brand: string; color: string; size: string
    priorQty: number; unitsSold60d: number; revenue60d: number; availableElsewhere: boolean
    stockoutDate: string; severity: 'critical' | 'warning'
}

function pctChange(curr: number, prev: number): number {
    if (prev === 0) return curr > 0 ? 100 : 0
    return ((curr - prev) / Math.abs(prev)) * 100
}
function getGreeting(): string {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    return 'Good evening'
}

export default function InventoryDashboardPage() {
    const user = useAuthStore(s => s.user)
    const token = useAuthStore(s => s.accessToken)
    const isAdmin = ['HeadOffice', 'SuperAdmin'].includes(user?.role ?? '')
    const qc = useQueryClient()

    const {
        layouts, activeWidgets, widgetSettings, isEditing, layoutVersion,
        availableLayouts, currentLayoutId, currentLayoutName, currentLayoutSource,
        setEditing, removeWidget, setLayouts, snapshotLayouts,
        revertToInitial, updateWidgetSettings, resetLayout, resetToSystemDefault,
        loadFromServer, loadLayout, saveToAccount, saveAsSystemDefault,
        hasUnsavedChanges, applyImportedLayout,
    } = useInventoryDashboardLayoutStore()

    const { data: storeConfig, isLoading: configLoading } = useQuery<StoreConfig>({
        queryKey: ['dashboard-stores'],
        queryFn: () => api('/api/dashboard/stores', token),
        staleTime: 60_000,
    })

    const [searchParams, setSearchParams] = useSearchParams()

    const [storeId, setStoreId] = useState<string>(searchParams.get('storeId') || 'group')
    const [brandId, setBrandId] = useState<string>(searchParams.get('brandId') || '')
    const [department, setDepartment] = useState<string>(searchParams.get('department') || '')
    const [subdept, setSubdept] = useState<string>(searchParams.get('subdept') || '')
    const [gender, setGender] = useState<string>(searchParams.get('gender') || '')
    const [selectedSnapshot, setSelectedSnapshot] = useState<string>(searchParams.get('snapshot') || '')
    const [snapshotLimit, setSnapshotLimit] = useState<string>(searchParams.get('limit') || 'ALL')
    const includeLive = true

    const [showDebug, setShowDebug] = useState(false)
    const [actionMenuOpen, setActionMenuOpen] = useState(false)
    const [showGallery, setShowGallery] = useState(false)
    const [showSaveDialog, setShowSaveDialog] = useState(false)
    const [saveLayoutName, setSaveLayoutName] = useState('')
    const [saveLayoutIsNew, setSaveLayoutIsNew] = useState(false)
    const [saveLayoutIsDefault, setSaveLayoutIsDefault] = useState(false)
    const [overwriteSystemDefault, setOverwriteSystemDefault] = useState(false)
    const [overwriteUserCustomizations, setOverwriteUserCustomizations] = useState(false)
    const [showResetConfirm, setShowResetConfirm] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState<string | null>(null)

    const greeting = getGreeting()

    useEffect(() => {
        if (!storeConfig) return
        // A single-store, non-privileged user has no "group"/"all" option to represent in the picker,
        // so bind directly to their store — otherwise the <select> would display that store while the
        // state stays "group", and the queries would silently run unscoped.
        if (!storeConfig.canViewAll && storeConfig.stores.length === 1) {
            setStoreId(storeConfig.stores[0].id)
        } else {
            setStoreId(storeConfig.defaultView)
        }
    }, [storeConfig])
    // Load layout from server
    useEffect(() => {
        if (token && user) {
            const requestedLayoutId = new URLSearchParams(window.location.search).get('layout')
            if (requestedLayoutId) {
                loadLayout(token, user.id, 'inventory', requestedLayoutId)
            } else {
                loadFromServer(token, user.id, 'inventory')
            }
        }
    }, [token, user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

    // Sync state to URL
    useEffect(() => {
        const params = new URLSearchParams()
        if (currentLayoutId) params.set('layout', currentLayoutId)
        if (storeId !== 'group') params.set('storeId', storeId)
        if (brandId) params.set('brandId', brandId)
        if (department) params.set('department', department)
        if (subdept) params.set('subdept', subdept)
        if (gender) params.set('gender', gender)
        if (selectedSnapshot) params.set('snapshot', selectedSnapshot)
        if (snapshotLimit !== 'ALL') params.set('limit', snapshotLimit)
        
        setSearchParams(params, { replace: true })
    }, [currentLayoutId, storeId, brandId, department, subdept, gender, selectedSnapshot, snapshotLimit, setSearchParams])


    const filterParams = useMemo(() => {
        const p = new URLSearchParams()
        // Always send the literal selection (guid | "group" | "all"); the API resolves it
        // against the user's role + store assignments and enforces authorization.
        p.set('storeId', storeId || 'group')
        if (brandId) p.set('brandId', brandId)
        if (department) p.set('department', department)
        if (subdept) p.set('subdept', subdept)
        if (gender) p.set('gender', gender)
        return p.toString()
    }, [storeId, brandId, department, subdept, gender])

    const isLiveSelected = selectedSnapshot === 'LIVE'
    const snapshotParam = selectedSnapshot && !isLiveSelected ? `&snapshotDate=${selectedSnapshot}` : ''
    const liveParam = includeLive ? '&includeLive=true' : ''
    const liveModeParam = isLiveSelected ? '&snapshotMode=LIVE' : ''

    const { data: snapshots } = useQuery<SnapshotDate[]>({
        queryKey: ['inventory-snapshots', includeLive], queryFn: () => api(`/api/inventory/snapshots?${liveParam}`, token), staleTime: 300_000,
    })

    // Auto-default selectedSnapshot to first available snapshot when empty
    useEffect(() => {
        if (snapshots && snapshots.length > 0 && !selectedSnapshot) {
            setSelectedSnapshot(snapshots[0].date)
        }
    }, [snapshots, selectedSnapshot])

    // Generate months options dynamically: ALL (maxMonths), multiples of 12 down to 24, then 18, 12, 6
    const showOptions = useMemo(() => {
        if (!snapshots) return [{ value: 'ALL', label: 'ALL' }, { value: '12', label: '12' }]
        const maxMonths = snapshots.filter(s => s.date !== 'LIVE').length
        
        const options: { value: string; label: string }[] = []
        options.push({ value: 'ALL', label: `ALL (${maxMonths})` })
        
        // Find highest multiple of 12 that is <= maxMonths
        const start12 = Math.floor(maxMonths / 12) * 12
        for (let val = start12; val >= 24; val -= 12) {
            if (val <= maxMonths) {
                options.push({ value: String(val), label: String(val) })
            }
        }
        
        // Push final three increments: 18, 12, 6
        const finalThree = [18, 12, 6]
        for (const val of finalThree) {
            if (val < maxMonths) {
                options.push({ value: String(val), label: String(val) })
            }
        }
        
        return options
    }, [snapshots])

    // Defensive fallback: Reset selected limit to 'ALL' if it's no longer present in the dynamically calculated options
    useEffect(() => {
        const optionValues = showOptions.map(o => o.value)
        if (snapshotLimit !== 'ALL' && optionValues.length > 1 && !optionValues.includes(snapshotLimit)) {
            setSnapshotLimit('ALL')
        }
    }, [showOptions, snapshotLimit])

    const { data: brands } = useQuery<Brand[]>({
        queryKey: ['inventory-brands', storeId, department, subdept, gender, selectedSnapshot, includeLive], 
        queryFn: () => api(`/api/inventory/brands?${filterParams}${snapshotParam}${liveModeParam}`, token), staleTime: 300_000,
    })
    const { data: departments } = useQuery<string[]>({
        queryKey: ['inventory-departments'], queryFn: () => api('/api/inventory/departments', token), staleTime: 300_000,
    })
    const { data: subdepartments } = useQuery<string[]>({
        queryKey: ['inventory-subdepts', department],
        queryFn: () => api(`/api/inventory/subdepartments${department ? `?department=${encodeURIComponent(department)}` : ''}`, token),
        staleTime: 300_000,
    })
    const { data: genders } = useQuery<string[]>({
        queryKey: ['inventory-genders', department],
        queryFn: () => api(`/api/inventory/genders${department ? `?department=${encodeURIComponent(department)}` : ''}`, token),
        staleTime: 300_000,
    })
    const { data: levels, isFetching: levelsLoading, dataUpdatedAt } = useQuery<InventoryLevelPoint[]>({
        queryKey: ['inventory-levels', storeId, brandId, department, subdept, gender, includeLive],
        queryFn: () => api(`/api/inventory/levels?${filterParams}${liveParam}`, token),
        staleTime: 60_000, placeholderData: keepPreviousData
    })

    const visibleLevels = useMemo(() => {
        if (!levels || levels.length === 0) return []
        if (snapshotLimit === 'ALL') return levels

        const limitVal = parseInt(snapshotLimit, 10)
        if (isNaN(limitVal) || limitVal >= levels.length) return levels

        const activeIdx = levels.findIndex((pt) => {
            if (!selectedSnapshot) return false
            if (selectedSnapshot === 'LIVE') {
                return pt.period === 'Live'
            }
            return pt.period === selectedSnapshot || pt.period.startsWith(selectedSnapshot) || selectedSnapshot.startsWith(pt.period)
        })

        if (activeIdx === -1) {
            // Default to showing the latest limitVal months
            return levels.slice(levels.length - limitVal)
        }

        let startIdx = activeIdx - Math.floor(limitVal / 2)
        if (startIdx < 0) {
            startIdx = 0
        } else if (startIdx + limitVal > levels.length) {
            startIdx = Math.max(0, levels.length - limitVal)
        }

        return levels.slice(startIdx, startIdx + limitVal)
    }, [levels, snapshotLimit, selectedSnapshot])
    const { data: summary, isFetching: summaryLoading } = useQuery<InventorySummary>({
        queryKey: ['inventory-summary', storeId, brandId, department, subdept, gender, selectedSnapshot, includeLive],
        queryFn: () => api(`/api/inventory/summary?${filterParams}${snapshotParam}${liveModeParam}`, token), 
        staleTime: 60_000, placeholderData: keepPreviousData
    })
    const { data: stockouts, isLoading: stockoutsLoading } = useQuery<StockoutItem[]>({
        queryKey: ['inventory-stockouts', filterParams, selectedSnapshot, includeLive],
        queryFn: () => api(`/api/inventory/stockouts?${filterParams}${snapshotParam}${liveModeParam}`, token), placeholderData: keepPreviousData,
    })
    const { data: deptBreakdown } = useQuery<any[]>({
        queryKey: ['inventory-dept-breakdown', storeId, brandId, gender, selectedSnapshot, includeLive],
        queryFn: () => api(`/api/inventory/department-breakdown?${filterParams}${snapshotParam}${liveModeParam}`, token),
        staleTime: 60_000, placeholderData: keepPreviousData,
    })

    const { data: debugData } = useQuery<any>({
        queryKey: ['inventory-debug', filterParams, selectedSnapshot],
        queryFn: () => api(`/api/inventory/debug-sql?${filterParams}${snapshotParam}`, token),
        enabled: showDebug
    })

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ['inventory-levels'] })
        qc.invalidateQueries({ queryKey: ['inventory-summary'] })
        qc.invalidateQueries({ queryKey: ['inventory-stockouts'] })
    }
    const handleFlushCache = async () => {
        try { await apiPost('/api/inventory/cache/flush', token); refresh() } catch { /* silent */ }
    }

    const handleSelectSnapshotFromChart = (period: string) => {
        if (period === 'LIVE' || period === 'Live') {
            setSelectedSnapshot('LIVE')
            return
        }
        const matched = snapshots?.find(s => s.date.startsWith(period))
        if (matched) {
            setSelectedSnapshot(matched.date)
        } else {
            setSelectedSnapshot(`${period}-01`)
        }
    }

    // ── Edit mode handlers ──────────────────────────────────────────
    const handleSave = () => setShowSaveDialog(true)
    const handleCancel = () => { revertToInitial() }
    const handleSaveAccount = async () => {
        const isNew = saveLayoutIsNew || !currentLayoutId
        await saveToAccount(token!, isNew ? saveLayoutName || 'Inventory Layout' : currentLayoutName, saveLayoutIsDefault, isNew, 'inventory')
        if (overwriteSystemDefault) await saveAsSystemDefault(token!, 'inventory', overwriteUserCustomizations)
        setShowSaveDialog(false); setEditing(false)
        setSaveLayoutName(''); setSaveLayoutIsNew(false); setSaveLayoutIsDefault(false)
        setOverwriteSystemDefault(false); setOverwriteUserCustomizations(false)
    }
    const handleReset = async () => {
        try { await resetToSystemDefault(token!, user!.id, 'inventory') } catch { resetLayout() }
        setShowResetConfirm(false)
    }
    const handlePresetSelect = (layoutId: string) => loadLayout(token!, user!.id, 'inventory', layoutId)
    const handleExportLayout = () => JSON.stringify({ layouts, activeWidgets, widgetSettings }, null, 2)
    const handleImportLayout = (json: string): string | null => {
        let parsed: any
        try { parsed = JSON.parse(json) } catch { return 'Invalid JSON syntax.' }
        if (!parsed.activeWidgets || !Array.isArray(parsed.activeWidgets)) return 'Missing "activeWidgets" array.'
        if (!parsed.layouts || typeof parsed.layouts !== 'object') return 'Missing "layouts" object.'
        applyImportedLayout(parsed); return null
    }

    // ── KPI helper with prior month + last year comparisons ────────
    const renderKpi = (label: string, value: number, priorValue: number, lyValue: number, accent: string, format: 'int' | 'currency' = 'int') => {
        const pctMom = pctChange(value, priorValue)
        const dirMom = pctMom > 0 ? 'up' : pctMom < 0 ? 'down' : 'flat'
        const pctLy = pctChange(value, lyValue)
        const dirLy = pctLy > 0 ? 'up' : pctLy < 0 ? 'down' : 'flat'
        return (
            <div className="kpi-card" style={{ '--kpi-accent': accent } as any}>
                <div className="kpi-label">{label}</div>
                <div className="kpi-value">{fmt(value, format)}</div>
                <div className="kpi-comparisons">
                    <div className="kpi-comparison">
                        <span className={`kpi-arrow ${dirMom}`}>{dirMom === 'up' ? '▲' : dirMom === 'down' ? '▼' : '–'}</span>
                        <span className={`kpi-pct ${dirMom}`}>{Math.abs(pctMom).toFixed(1)}%</span>
                        <span className="comp-label-full">vs prior month</span>
                        <span className="comp-label-short">m</span>
                        <span className="comp-val" style={{ color: 'var(--color-text-tertiary)' }}>({fmt(priorValue, format)})</span>
                    </div>
                    <div className="kpi-comparison">
                        <span className={`kpi-arrow ${dirLy}`}>{dirLy === 'up' ? '▲' : dirLy === 'down' ? '▼' : '–'}</span>
                        <span className={`kpi-pct ${dirLy}`}>{Math.abs(pctLy).toFixed(1)}%</span>
                        <span className="comp-label-full">vs last year</span>
                        <span className="comp-label-short">y</span>
                        <span className="comp-val" style={{ color: 'var(--color-text-tertiary)' }}>({fmt(lyValue, format)})</span>
                    </div>
                </div>
            </div>
        )
    }

    const snapshotLabel = useMemo(() => {
        if (!summary?.snapshotDate) return '...'
        const datePart = summary.snapshotDate.split('T')[0]
        const matched = snapshots?.find(s => s.date === datePart)
        if (matched) return matched.label

        // Fallback: safe parsing to prevent timezone shifts
        const parts = datePart.split('-')
        if (parts.length === 3) {
            const y = parseInt(parts[0], 10)
            const m = parseInt(parts[1], 10) - 1
            const d = parseInt(parts[2], 10)
            return new Date(y, m, d, 12, 0, 0).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        }
        return new Date(summary.snapshotDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    }, [summary?.snapshotDate, snapshots])

    // ── Widget renderer ─────────────────────────────────────────────
    const renderWidget = (widgetId: string) => {
        switch (widgetId) {
            case 'invTotalUnits':
                return summary ? renderKpi('Total Units on Hand', summary.totalUnits, summary.priorTotalUnits, summary.lyTotalUnits, 'var(--color-primary)') : null
            case 'invUniqueSKUs':
                return summary ? renderKpi('Unique SKUs', summary.uniqueSKUs, summary.priorUniqueSKUs, summary.lyUniqueSKUs, 'var(--color-info)') : null
            case 'invValue':
                return summary ? renderKpi('Inventory Value (Cost)', summary.inventoryValue, summary.priorInventoryValue, summary.lyInventoryValue, 'var(--color-success)', 'currency') : null
            case 'invMoMChange': {
                if (!summary) return null
                const unitChange = summary.totalUnits - summary.priorTotalUnits
                const pct = pctChange(summary.totalUnits, summary.priorTotalUnits)
                const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat'
                return (
                    <div className="kpi-card" style={{ '--kpi-accent': 'var(--color-warning)' } as any}>
                        <div className="kpi-label">Month-over-Month Change</div>
                        <div className="kpi-value" style={{ color: dir === 'up' ? 'var(--kpi-positive)' : dir === 'down' ? 'var(--kpi-negative)' : undefined }}>
                            {unitChange > 0 ? '+' : ''}{fmt(unitChange, 'int')}
                        </div>
                        <div className="kpi-comparisons"><div className="kpi-comparison">
                            <span className={`kpi-arrow ${dir}`}>{dir === 'up' ? '▲' : dir === 'down' ? '▼' : '–'}</span>
                            <span className={`kpi-pct ${dir}`}>{Math.abs(pct).toFixed(1)}%</span>
                            <span className="comp-label-full">units change</span>
                        </div></div>
                    </div>
                )
            }
            case 'invLevelChart': {
                const settings = widgetSettings['invLevelChart'] || { showUnits: true, showSKUs: false, showValue: false }
                return visibleLevels.length > 0 ? (
                    <InventoryLevelChart
                        data={visibleLevels}
                        settings={settings}
                        selectedSnapshot={selectedSnapshot}
                        onSelectSnapshot={handleSelectSnapshotFromChart}
                    />
                ) : null
            }
            case 'invSnapshotSummary': {
                if (!visibleLevels || visibleLevels.length < 2) return <div className="chart-panel" style={{ padding: 'var(--space-5)' }}><div className="chart-panel-title">Snapshot Summary</div><p style={{ color: 'var(--color-text-tertiary)' }}>Not enough data</p></div>
                const first = visibleLevels[0].totalQuantity, last = visibleLevels[visibleLevels.length - 1].totalQuantity
                const p = pctChange(last, first)
                const maxIdx = visibleLevels.reduce((mi, l, i, arr) => l.totalQuantity > arr[mi].totalQuantity ? i : mi, 0)
                const [y, m] = visibleLevels[maxIdx].period.split('-')
                const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
                return (
                    <div className="chart-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 'var(--space-5)' }}>
                        <div className="chart-panel-title">Snapshot Summary</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', marginTop: 'var(--space-3)' }}>
                            <div><div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Data Points</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text)', marginTop: 4 }}>{visibleLevels.length} months</div></div>
                            <div><div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Trend (First → Last)</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 700, marginTop: 4, color: p >= 0 ? 'var(--kpi-positive)' : 'var(--kpi-negative)' }}>{p > 0 ? '+' : ''}{p.toFixed(1)}%</div></div>
                            <div><div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Peak Inventory</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text)', marginTop: 4 }}>{fmt(Math.max(...visibleLevels.map(l => l.totalQuantity)), 'int')}</div>
                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 2 }}>{MONTHS[parseInt(m) - 1]} {y}</div></div>
                        </div>
                    </div>
                )
            }
            case 'invStockouts':
                return stockouts ? <StockoutInsightsPanel items={stockouts} /> : null
            case 'invDeptPie':
                return deptBreakdown ? <DepartmentPieChart data={deptBreakdown} metric="value" /> : null
            default:
                return <div style={{ padding: '1rem', color: 'var(--color-text-tertiary)' }}>Unknown widget: {widgetId}</div>
        }
    }

    const isLoading = (!levels && levelsLoading) || (!summary && summaryLoading)

    if (configLoading) {
        return (<div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem 0' }}>
            <div className="page-header"><h1>{greeting}, {user?.displayName.split(' ')[0]} 📦</h1></div>
            <div className="dashboard-loading"><div className="spinner" /><span>Loading configuration…</span></div>
        </div>)
    }

    return (
        <>
            {/* ── Page Header ───────────────────────────────────── */}
            <div className="page-header">
                <h1>{greeting}, {user?.displayName.split(' ')[0]} 📦</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 4, flexWrap: 'wrap' }}>
                    <select className="header-store-select" value={storeId} onChange={e => setStoreId(e.target.value)}>
                        {storeConfig?.stores
                            ?.filter(s => s.name !== "VPO Central" && s.name !== "VPO Online eStore")
                            ?.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
                        {storeConfig?.hasGroup && <option value="group">My Store Group</option>}
                        {storeConfig?.canViewAll && <option value="all">All Stores</option>}
                    </select>


                    {isLiveSelected ? (
                        <span className="live-indicator">
                            Live Inventory
                        </span>
                    ) : summary?.snapshotDate ? (
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', fontWeight: 400 }}>
                            · Latest snapshot: {snapshotLabel}
                        </span>
                    ) : null}
                </div>
            </div>

            {/* ── Filter Toolbar ─────────────────────────────────── */}
            <div className="dashboard-toolbar">
                {/* Left Side: Snapshot and Limit Controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <span className="toolbar-label">Snapshot</span>
                    <select value={selectedSnapshot} onChange={e => setSelectedSnapshot(e.target.value)}
                        style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none', maxWidth: 200 }}>
                        {snapshots?.map(s => (<option key={s.date} value={s.date}>{s.label}</option>))}
                    </select>

                    <span className="toolbar-label" style={{ marginLeft: 'var(--space-2)' }}>Months</span>
                    <select value={snapshotLimit} onChange={e => setSnapshotLimit(e.target.value)}
                        style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none' }}>
                        {showOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* Right Side: Filters & Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginLeft: 'auto', flexWrap: 'wrap' }}>
                    <span className="toolbar-label">Brand</span>
                    <select value={brandId} onChange={e => setBrandId(e.target.value)}
                        style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none', maxWidth: 200 }}>
                        <option value="">All Brands</option>
                        {brands?.map(b => (<option key={b.id} value={b.id}>{b.name}</option>))}
                    </select>

                    <span className="toolbar-label">Department</span>
                    <select value={department} onChange={e => { setDepartment(e.target.value); setSubdept(''); setGender('') }}
                        style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none', maxWidth: 200 }}>
                        <option value="">All Departments</option>
                        {departments?.map(d => (<option key={d} value={d}>{d}</option>))}
                    </select>

                    {department && subdepartments && subdepartments.length > 0 && (<>
                        <span className="toolbar-label">Sub-Dept</span>
                        <select value={subdept} onChange={e => setSubdept(e.target.value)}
                            style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none', maxWidth: 180 }}>
                            <option value="">All</option>
                            {subdepartments.map(s => (<option key={s} value={s}>{s}</option>))}
                        </select>
                    </>)}

                    {department && genders && genders.length > 0 && (<>
                        <span className="toolbar-label">Gender</span>
                        <select value={gender} onChange={e => setGender(e.target.value)}
                            style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none', maxWidth: 160 }}>
                            <option value="">All</option>
                            {genders.map(g => (<option key={g} value={g}>{g}</option>))}
                        </select>
                    </>)}

                    {!isEditing ? (
                        <div className="desktop-actions-menu-container" style={{ position: 'relative' }}>
                            <button className="toolbar-refresh-btn" onClick={() => setActionMenuOpen(x => !x)} title="Dashboard Actions">⚙ Actions</button>
                            {actionMenuOpen && (<>
                                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setActionMenuOpen(false)} />
                                <div className="dashboard-actions-menu">
                                    {dataUpdatedAt > 0 && (<div style={{ padding: '8px 12px', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border)', marginBottom: '4px' }}>Updated {new Date(dataUpdatedAt).toLocaleTimeString()}</div>)}
                                    <button className="nav-item" onClick={() => { refresh(); setActionMenuOpen(false) }} style={{ padding: '8px 12px' }}><span className="nav-icon">↻</span><span className="nav-label">Refresh</span></button>
                                    <button className="nav-item" onClick={() => { setEditing(true); setActionMenuOpen(false) }} style={{ padding: '8px 12px' }}><span className="nav-icon">✏️</span><span className="nav-label">Edit Layout</span></button>
                                    {isAdmin && (<button className="nav-item" onClick={() => { setShowDebug(true); setActionMenuOpen(false) }} style={{ padding: '8px 12px', color: 'var(--color-text-tertiary)' }}><span className="nav-icon">🐛</span><span className="nav-label">Debug SQL</span></button>)}
                                    {isAdmin && (<button className="nav-item" onClick={() => { handleFlushCache(); setActionMenuOpen(false) }} style={{ padding: '8px 12px', color: 'var(--color-warning)' }}><span className="nav-icon">🗑</span><span className="nav-label">Flush Cache</span></button>)}
                                </div>
                            </>)}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            <button className="edit-mode-btn" onClick={() => setShowGallery(true)}>➕ Add Widget</button>
                            <LayoutPresetsMenu availableLayouts={availableLayouts} currentLayoutId={currentLayoutId} currentLayoutSource={currentLayoutSource}
                                isEditing={isEditing} hasUnsavedChanges={hasUnsavedChanges()} dashboardType="inventory"
                                onSelectLayout={handlePresetSelect} onExport={handleExportLayout} onImport={handleImportLayout} onSaveFirst={handleSave} />
                            <button className="edit-mode-btn" onClick={() => setShowResetConfirm(true)} style={{ borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>↺ Reset</button>
                            <button className="edit-mode-btn cancel" onClick={handleCancel}>✕ Cancel</button>
                            <button className="edit-mode-btn done" onClick={handleSave}>💾 Save</button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Widget Gallery ────────────────────────────────── */}
            {showGallery && <WidgetGallery onClose={() => setShowGallery(false)} dashboardType="inventory" />}

            {/* ── Save Dialog ──────────────────────────────────── */}
            {showSaveDialog && (
                <div className="admin-save-overlay" onClick={() => setShowSaveDialog(false)}>
                    <div className="admin-save-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Save Layout</h3>
                        <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', cursor: currentLayoutId ? 'pointer' : 'not-allowed', color: currentLayoutId ? 'inherit' : 'var(--color-text-tertiary)' }}>
                                <input type="radio" checked={!saveLayoutIsNew} onChange={() => setSaveLayoutIsNew(false)} disabled={!currentLayoutId} style={{ marginRight: '8px' }} />
                                Overwrite Current Layout {currentLayoutName ? `(${currentLayoutName})` : ''}
                            </label>
                            <label style={{ display: 'block', fontSize: '14px', cursor: 'pointer' }}>
                                <input type="radio" checked={saveLayoutIsNew || !currentLayoutId} onChange={() => setSaveLayoutIsNew(true)} style={{ marginRight: '8px' }} />
                                Save as New Layout
                            </label>
                        </div>
                        {(saveLayoutIsNew || !currentLayoutId) && (
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', fontSize: '12px', color: 'var(--color-text-tertiary)', marginBottom: '4px' }}>Layout Name</label>
                                <input type="text" value={saveLayoutName} onChange={e => setSaveLayoutName(e.target.value)} placeholder="My Inventory Layout"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: '14px' }} autoFocus />
                            </div>
                        )}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label className="admin-save-checkbox"><input type="checkbox" checked={saveLayoutIsDefault} onChange={e => setSaveLayoutIsDefault(e.target.checked)} /> Set as my default</label>
                        </div>
                        <div className="admin-save-actions">
                            <button className="btn btn-primary" onClick={handleSaveAccount} disabled={(saveLayoutIsNew || !currentLayoutId) && !saveLayoutName.trim()}>Save Layout</button>
                        </div>
                        {isAdmin && (<>
                            <div className="admin-save-divider" />
                            <h4 style={{ marginBottom: '0.5rem' }}>Admin Options</h4>
                            <label className="admin-save-checkbox"><input type="checkbox" checked={overwriteSystemDefault} onChange={e => { setOverwriteSystemDefault(e.target.checked); if (!e.target.checked) setOverwriteUserCustomizations(false) }} /> Overwrite System Default</label>
                            <label className="admin-save-checkbox" style={{ marginTop: '8px', opacity: overwriteSystemDefault ? 1 : 0.5 }}><input type="checkbox" checked={overwriteUserCustomizations} onChange={e => { if (overwriteSystemDefault) setOverwriteUserCustomizations(e.target.checked) }} /> Also overwrite user customizations</label>
                        </>)}
                    </div>
                </div>
            )}

            {/* ── Debug Modal ───────────────────────────────────────── */}
            {showDebug && <SqlDebugModal queries={debugData?.debug ?? []} activeProfile={undefined} connectionInfo={undefined} onClose={() => setShowDebug(false)} />}

            {/* ── Reset Confirm ─────────────────────────────────── */}
            {showResetConfirm && (
                <div className="admin-save-overlay" onClick={() => setShowResetConfirm(false)}>
                    <div className="admin-save-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Reset Layout</h3>
                        <p style={{ margin: '1rem 0' }}>This will revert your inventory dashboard to the system default layout. Continue?</p>
                        <div className="admin-save-actions">
                            <button className="btn btn-secondary" onClick={() => setShowResetConfirm(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleReset} style={{ background: 'var(--color-error)' }}>Reset</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Loading State ──────────────────────────────────── */}
            {isLoading && (<div className="dashboard-loading"><div className="spinner" /><span>Loading inventory data…</span></div>)}

            {/* ── Widget Grid ────────────────────────────────────── */}
            {!isLoading && (
                <WidgetGrid
                    layouts={layouts} activeWidgets={activeWidgets} widgetSettings={widgetSettings}
                    isEditing={isEditing} layoutVersion={layoutVersion}
                    setLayouts={setLayouts} snapshotLayouts={snapshotLayouts} removeWidget={removeWidget}
                    updateWidgetSettings={updateWidgetSettings} renderWidget={renderWidget}
                    settingsOpen={settingsOpen} setSettingsOpen={setSettingsOpen}
                    isFetching={levelsLoading || summaryLoading || stockoutsLoading}
                />
            )}
        </>
    )
}
